<template>
  <b-modal
    v-model="isVisible"
    modal-class="modal-fullscreen coach-performance-review-modal"
    size="lg"
    hide-footer
    title="Performance Review"
    @hidden="onModalHidden"
  >
    <!-- Header with Date and Status -->
    <div class="review-header">
      <h4 class="review-meta">
        <span class="review-date">{{
          formatDate(reportData.created_date)
        }}</span>
        <span class="status-badge" :class="statusClass">{{
          reportData.status || "Draft"
        }}</span>
      </h4>

      <!-- Auto-save indicator -->
      <div v-if="hasUnsavedChanges" class="autosave-indicator">
        <i class="bi bi-circle-fill text-warning"></i>
        <span>Unsaved changes</span>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p>Loading review data...</p>
    </div>

    <!-- Main Content -->
    <div v-else class="signed-report">
      <!-- Player Review Section -->
      <div class="player-signed-report">
        <div class="section-header">
          <h4><i class="bi bi-person-fill"></i> Player Review</h4>
        </div>

        <!-- Player Info Card -->
        <div class="user-info-card two-col-pop">
          <div class="user-photo-info">
            <img
              v-if="playerData.userImage"
              :src="getImageUrl(playerData.userImage)"
              :alt="playerData.name"
              class="player-photo"
            />
            <TenantLogo v-else logo-class="login-logo" />
          </div>
          <div class="user-details-pop">
            <p><span class="label">Name:</span> {{ playerData.name }}</p>
            <p><span class="label">Phone:</span> {{ playerData.phone }}</p>
            <p><span class="label">Email:</span> {{ playerData.email }}</p>
            <p>
              <span class="label">DOB:</span> {{ formatDate(playerData.dob) }}
            </p>
            <p>
              <span class="label">Position:</span> {{ playerData.position }}
            </p>
          </div>
        </div>

        <!-- Player Review Form - Text Based -->
        <div
          class="review-wrapper"
          v-if="showPlayerReviewForm && !isTrafficLightReview"
        >
          <!-- Technical -->
          <ReviewSection
            label="Technical"
            icon="bi-gear"
            v-model="reportData.technical_player_comments"
            :readonly="isPlayerReviewReadonly"
            placeholder="Add technical feedback (e.g., passing, shooting, first touch, dribbling)"
          />

          <!-- Tactical -->
          <ReviewSection
            label="Tactical"
            icon="bi-diagram-3"
            v-model="reportData.tactical_player_comments"
            :readonly="isPlayerReviewReadonly"
            placeholder="Add tactical feedback (e.g., positioning, decision making, awareness)"
          />

          <!-- Physical -->
          <ReviewSection
            label="Physical"
            icon="bi-lightning"
            v-model="reportData.physical_player_comments"
            :readonly="isPlayerReviewReadonly"
            placeholder="Add physical feedback (e.g., speed, strength, endurance, agility)"
          />

          <!-- Mental/Psychological -->
          <ReviewSection
            label="Mental"
            icon="bi-brain"
            v-model="reportData.physcological_player_comments"
            :readonly="isPlayerReviewReadonly"
            placeholder="Add mental feedback (e.g., confidence, focus, resilience, attitude)"
          />

          <!-- Overall Comments -->
          <ReviewSection
            label="Overall Comments"
            icon="bi-chat-quote"
            v-model="reportData.player_comments"
            :readonly="isPlayerReviewReadonly"
            placeholder="Add overall comments and summary"
            class="overall-comments"
          />
        </div>

        <!-- Player Review Form - Traffic Light -->
        <div
          class="review-wrapper traffic-light-review"
          v-else-if="
            showPlayerReviewForm &&
            isTrafficLightReview &&
            trafficLightPlayerData
          "
        >
          <div class="traffic-light-header">
            <span class="player-type-indicator">
              <i
                class="bi"
                :class="
                  detectedPlayerType === 'goalkeeper'
                    ? 'bi-person-arms-up'
                    : 'bi-person-walking'
                "
              ></i>
              {{
                detectedPlayerType === "goalkeeper"
                  ? "Goalkeeper"
                  : "Outfield Player"
              }}
              Assessment
            </span>
          </div>
          <TrafficLightSection
            v-for="category in trafficLightCategories"
            :key="`player_${category.key}`"
            :category="category.key"
            :player-type="detectedPlayerType"
            v-model="trafficLightPlayerData[category.key]"
            :readonly="isPlayerReviewReadonly"
            :data-category="category.key"
          />
          <!-- Overall Comments for Traffic Light -->
          <ReviewSection
            label="Overall Comments"
            icon="bi-chat-quote"
            v-model="reportData.player_comments"
            :readonly="isPlayerReviewReadonly"
            placeholder="Add any additional comments"
            class="overall-comments"
          />
        </div>
        <div v-else-if="isPlayer && !reportData.id" class="no-report-message">
          <i class="bi bi-info-circle"></i>
          <p>
            No player report saved yet. Your coach will create a review for you.
          </p>
        </div>

        <!-- Player Signature -->
        <div
          v-if="showSignatures"
          class="signature-section players-charter charter"
        >
          <SignatureCapture
            title="Player Signature"
            signature-id="playerReportSignature"
            :signature-image="reportData.playerReportSignature"
            v-model="signatureState.player_signature_change"
            :editable="!isPublished"
            :saving="savingSignature === 'playerReportSignature'"
            @save="handleSignatureSave"
            @edit="handleSignatureEdit"
            @cancel="handleSignatureCancel"
          />
        </div>

        <!-- Parent Signature -->
        <div
          v-if="showSignatures"
          class="signature-section players-charter charter"
        >
          <SignatureCapture
            title="Parent Signature"
            signature-id="parentReportSignature"
            :signature-image="reportData.parentReportSignature"
            v-model="signatureState.parent_signature_change"
            :editable="!isPublished"
            :saving="savingSignature === 'parentReportSignature'"
            @save="handleSignatureSave"
            @edit="handleSignatureEdit"
            @cancel="handleSignatureCancel"
          />
        </div>
      </div>

      <!-- Coach Review Section -->
      <div v-if="showCoachSection" class="coach-signed-report">
        <div class="section-header">
          <h4><i class="bi bi-person-badge-fill"></i> Coach Review</h4>
        </div>

        <!-- Coach Info Card -->
        <div class="user-info-card two-col-pop">
          <div class="user-photo-info">
            <img
              v-if="reportData.userImage"
              :src="getImageUrl(reportData.userImage)"
              :alt="reportData.name"
              class="coach-photo"
            />
            <TenantLogo v-else logo-class="login-logo" />
          </div>
          <div class="user-details-pop">
            <p><span class="label">Coach name:</span> {{ reportData.name }}</p>
          </div>
        </div>

        <!-- Coach Review Form - Text Based -->
        <div class="review-wrapper" v-if="!isTrafficLightReview">
          <!-- Technical -->
          <ReviewSection
            label="Technical"
            icon="bi-gear"
            v-model="reportData.technical_coach_comments"
            :readonly="isCoachReviewReadonly"
            placeholder="Add technical feedback (e.g., passing, shooting, first touch, dribbling)"
          />

          <!-- Tactical -->
          <ReviewSection
            label="Tactical"
            icon="bi-diagram-3"
            v-model="reportData.tactical_coach_comments"
            :readonly="isCoachReviewReadonly"
            placeholder="Add tactical feedback (e.g., positioning, decision making, awareness)"
          />

          <!-- Physical -->
          <ReviewSection
            label="Physical"
            icon="bi-lightning"
            v-model="reportData.physical_coach_comments"
            :readonly="isCoachReviewReadonly"
            placeholder="Add physical feedback (e.g., speed, strength, endurance, agility)"
          />

          <!-- Mental/Psychological -->
          <ReviewSection
            label="Mental"
            icon="bi-brain"
            v-model="reportData.physcological_coach_comments"
            :readonly="isCoachReviewReadonly"
            placeholder="Add mental feedback (e.g., confidence, focus, resilience, attitude)"
          />

          <!-- Overall Comments -->
          <ReviewSection
            label="Overall Comments"
            icon="bi-chat-quote"
            v-model="reportData.coach_comments"
            :readonly="isCoachReviewReadonly"
            placeholder="Add overall comments and summary"
            class="overall-comments"
          />
        </div>

        <!-- Coach Review Form - Traffic Light -->
        <div
          class="review-wrapper traffic-light-review"
          v-else-if="isTrafficLightReview && trafficLightCoachData"
        >
          <div class="traffic-light-header">
            <span class="player-type-indicator">
              <i
                class="bi"
                :class="
                  detectedPlayerType === 'goalkeeper'
                    ? 'bi-person-arms-up'
                    : 'bi-person-walking'
                "
              ></i>
              {{
                detectedPlayerType === "goalkeeper"
                  ? "Goalkeeper"
                  : "Outfield Player"
              }}
              Assessment
            </span>
          </div>
          <TrafficLightSection
            v-for="category in trafficLightCategories"
            :key="`coach_${category.key}`"
            :category="category.key"
            :player-type="detectedPlayerType"
            v-model="trafficLightCoachData[category.key]"
            :readonly="isCoachReviewReadonly"
            :data-category="category.key"
          />
          <!-- Overall Comments for Traffic Light -->
          <ReviewSection
            label="Overall Comments"
            icon="bi-chat-quote"
            v-model="reportData.coach_comments"
            :readonly="isCoachReviewReadonly"
            placeholder="Add any additional comments"
            class="overall-comments"
          />
        </div>

        <!-- Coach Signature -->
        <div
          v-if="showSignatures"
          class="signature-section players-charter charter"
        >
          <SignatureCapture
            title="Coach Signature"
            signature-id="coachSignature"
            :signature-image="reportData.coachSignature"
            v-model="signatureState.coach_signature_change"
            :editable="!isPublished"
            :saving="savingSignature === 'coachSignature'"
            @save="handleSignatureSave"
            @edit="handleSignatureEdit"
            @cancel="handleSignatureCancel"
          />
        </div>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="buttons-wrapper action-footer">
      <!-- Status Selector (Admin/Coach only) -->
      <b-form-select
        v-if="canChangeStatus"
        v-model="reportData.status"
        @change="handleStatusChange"
        class="status-select"
      >
        <b-form-select-option :value="null">Select status</b-form-select-option>
        <b-form-select-option value="Draft">Draft</b-form-select-option>
        <b-form-select-option value="live">Live</b-form-select-option>
      </b-form-select>

      <!-- Save Button (New Report) -->
      <b-button
        v-if="!reportData.id"
        size="lg"
        variant="outline-success"
        @click="handleSave"
        :disabled="saving || $store.getters.isReadOnlySeason"
      >
        <i class="bi bi-file-text" aria-hidden="true"></i>
        {{ saving ? "Saving..." : "Save Report" }}
      </b-button>

      <!-- Update Button (Existing Report) -->
      <b-button
        v-if="reportData.id && !isPublished"
        size="lg"
        variant="outline-success"
        @click="handleUpdate"
        :disabled="saving || $store.getters.isReadOnlySeason"
      >
        <i class="bi bi-file-text" aria-hidden="true"></i>
        {{ saving ? "Updating..." : "Update Report" }}
      </b-button>

      <!-- Publish Button -->
      <b-button
        v-if="canPublish"
        size="lg"
        variant="outline-success"
        @click="handlePublish"
        :disabled="saving || $store.getters.isReadOnlySeason"
      >
        <i class="bi bi-file-earmark-check" aria-hidden="true"></i>
        Publish Report
      </b-button>

      <!-- Unpublish Button -->
      <b-button
        v-if="canUnpublish"
        size="lg"
        variant="outline-danger"
        @click="handleUnpublish"
        :disabled="saving || $store.getters.isReadOnlySeason"
      >
        <i class="bi bi-file-earmark-x" aria-hidden="true"></i>
        Unpublish Report
      </b-button>

      <!-- Print/PDF Button -->
      <b-button
        v-if="isPublished && !isPlayer"
        size="lg"
        variant="outline-primary"
        @click="handlePrint"
      >
        <i class="bi bi-printer" aria-hidden="true"></i>
        Print Report
      </b-button>

      <!-- Delete Button (Admin only) -->
      <b-button
        v-if="canDelete"
        size="lg"
        variant="outline-danger"
        @click="handleDelete"
        :disabled="saving || $store.getters.isReadOnlySeason"
      >
        <i class="bi bi-trash" aria-hidden="true"></i>
        Delete
      </b-button>
    </div>

    <!-- PDF Preview Modal -->
    <b-modal
      v-model="showPdfPreview"
      size="xl"
      hide-footer
      title="Report Preview"
    >
      <div class="pdf-preview-container">
        <iframe :src="pdfPreviewUrl" class="pdf-iframe" />
      </div>
    </b-modal>
  </b-modal>
</template>

<script>
import { QuillEditor } from "@vueup/vue-quill";
import moment from "moment";
import JsPDF from "jspdf";
import TenantLogo from "@/components/TenantLogo";
import SignatureCapture from "@/components/globals/SignatureCapture";
import CoachPerformanceReviewService from "@/services/coach-performance-review-service";
import ReviewSection from "@/components/profile/ReviewSection.vue";
import TrafficLightSection from "@/components/profile/TrafficLightSection.vue";
import {
  detectPlayerType,
  createEmptyTrafficLightData,
  getTrafficLightSummary,
  CATEGORIES,
} from "@/config/traffic-light-review-config";

export default {
  name: "CoachPerformanceReviewModal",
  components: {
    QuillEditor,
    TenantLogo,
    SignatureCapture,
    ReviewSection,
    TrafficLightSection,
  },
  props: {
    // Modal visibility (v-model)
    modelValue: {
      type: Boolean,
      default: false,
    },
    // Player data being reviewed
    playerData: {
      type: Object,
      required: true,
    },
    // Current logged in user data
    userData: {
      type: Object,
      required: true,
    },
    // Report data (can be empty for new, or populated for edit)
    initialReportData: {
      type: Object,
      default: () => ({}),
    },
    // Whether this is a coach view (vs player view)
    viewType: {
      type: String,
      default: "Player", // 'Player' or 'Coach'
      validator: (value) => ["Player", "Coach"].includes(value),
    },
    // Review format type: 'text' or 'traffic_light'
    reviewType: {
      type: String,
      default: "text",
      validator: (value) => ["text", "traffic_light"].includes(value),
    },
    // Season dates for filtering
    seasonStartDate: {
      type: String,
      default: null,
    },
    seasonEndDate: {
      type: String,
      default: null,
    },
  },
  emits: [
    "update:modelValue",
    "report-saved",
    "report-updated",
    "report-deleted",
    "report-published",
    "report-unpublished",
  ],
  data() {
    return {
      loading: false,
      saving: false,
      savingSignature: null,
      reportData: CoachPerformanceReviewService.getEmptyReportData(),
      signatureState: CoachPerformanceReviewService.getDefaultSignatureState(),
      originalReportData: null,
      showPdfPreview: false,
      pdfPreviewUrl: "",
      // Traffic light specific data
      trafficLightPlayerData: null,
      trafficLightCoachData: null,
    };
  },
  computed: {
    isVisible: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    isTrafficLightReview() {
      // Handle both 'traffic-light' and 'traffic_light' formats for backwards compatibility
      const propType = this.reviewType;
      const dataType = this.reportData?.review_type;
      return (
        propType === "traffic-light" ||
        propType === "traffic_light" ||
        dataType === "traffic-light" ||
        dataType === "traffic_light"
      );
    },
    detectedPlayerType() {
      return detectPlayerType(this.playerData?.position);
    },
    trafficLightCategories() {
      return Object.values(CATEGORIES);
    },
    isPublished() {
      return this.reportData.status === "published";
    },
    showSignatures() {
      return (
        this.reportData.status === "live" ||
        this.reportData.status === "published"
      );
    },
    showCoachSection() {
      // Show coach section for:
      // 1. Coaches/Admins (they can always see/edit coach section)
      // 2. Players viewing a published report
      console.log("Determining showCoachSection:", {
        viewType: this.viewType,
        isPublished: this.isPublished,
        isAdmin: this.isAdmin,
        isCoach: this.isCoach,
        result: this.isAdmin || this.isCoach || this.isPublished,
      });
      return this.isAdmin || this.isCoach || this.isPublished;
    },
    isPlayerReviewReadonly() {
      // Published reports are always readonly
      if (this.isPublished) return true;
      // Coaches/admins can edit player section in draft, players can edit when live
      if (this.isPlayer) {
        // Players can only edit when status is 'live'
        return this.reportData.status !== "Draft";
      }
      // Coaches/admins can always edit player section (unless published)
      return false;
    },
    isPlayer() {
      return this.userData?.userType === "User";
    },
    isCoach() {
      return this.userData?.userType === "Coach";
    },
    isAdmin() {
      return this.userData?.userType === "Admin";
    },
    isCoachReviewReadonly() {
      console.log("Determining isCoachReviewReadonly:", {
        isPublished: this.isPublished,
        isPlayer: this.isPlayer,
      });
      // Published reports are always readonly
      if (this.isPublished) return true;
      // Players cannot edit coach section
      if (this.isPlayer) return true;
      // Coaches/admins can edit
      return false;
    },
    canChangeStatus() {
      return this.isAdmin || (this.isCoach && !this.isPublished);
    },
    canPublish() {
      return (
        !this.isPublished &&
        this.reportData.player_signature_id !== null &&
        this.reportData.coach_signature_id !== null
      );
    },
    canUnpublish() {
      return this.isPublished && !this.isPlayer;
    },
    canDelete() {
      return this.isAdmin && this.reportData.id;
    },
    hasUnsavedChanges() {
      if (!this.originalReportData) return false;
      return (
        JSON.stringify(this.reportData) !==
        JSON.stringify(this.originalReportData)
      );
    },
    statusClass() {
      const status = this.reportData.status?.toLowerCase();
      return {
        "status-draft": status === "draft",
        "status-live": status === "live",
        "status-published": status === "published",
      };
    },
    showPlayerReviewForm() {
      console.log("Determining showPlayerReviewForm:", {
        isAdmin: this.isAdmin,
        isCoach: this.isCoach,
        isPlayer: this.isPlayer,
        reportId: this.reportData.id,
        reportStatus: this.reportData.status,
      });
      // Always show for coaches/admins (they can fill in player section)
      // For players: show if report exists OR if status allows editing
      if (this.isAdmin || this.isCoach) {
        return true;
      }
      // Players see the form if there's a report to view or if it's live (they can fill it in)
      return (
        this.reportData.id !== undefined || this.reportData.status === "live"
      );
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.initializeReport();
        }
      },
    },
    initialReportData: {
      immediate: true,
      deep: true,
      handler(newVal) {
        if (newVal && Object.keys(newVal).length > 0) {
          this.reportData = { ...this.reportData, ...newVal };
          this.originalReportData = JSON.parse(JSON.stringify(this.reportData));
        }
      },
    },
    // Initialize traffic light data when reviewType prop changes
    reviewType: {
      immediate: true,
      handler(newVal) {
        if (
          (newVal === "traffic-light" || newVal === "traffic_light") &&
          this.modelValue
        ) {
          if (!this.trafficLightPlayerData || !this.trafficLightCoachData) {
            this.initializeTrafficLightData();
          }
        }
      },
    },
  },
  methods: {
    async initializeReport() {
      // Only load signatures if we have an existing report with a valid ID
      const hasExistingReport =
        this.initialReportData?.id &&
        typeof this.initialReportData.id === "number" &&
        this.initialReportData.id > 0;

      if (hasExistingReport) {
        this.loading = true;
        try {
          await this.loadSignatures(this.initialReportData.id);
          this.reportData = { ...this.reportData, ...this.initialReportData };

          // Initialize traffic light data from existing report
          // Check both the prop and the data's review_type, or if traffic_light_data exists
          const hasTrafficLightData =
            this.initialReportData?.traffic_light_data;
          if (this.isTrafficLightReview || hasTrafficLightData) {
            this.initializeTrafficLightData(this.initialReportData);
          }

          this.originalReportData = JSON.parse(JSON.stringify(this.reportData));
        } catch (error) {
          console.error("Failed to load report:", error);
          this.$toast?.error?.("Failed to load report data");
        } finally {
          this.loading = false;
        }
      } else {
        // New report - reset everything
        this.reportData = {
          ...CoachPerformanceReviewService.getEmptyReportData(),
          created_date: moment().format("YYYY-MM-DD"),
          user_id: this.playerData?.id,
          review_type: this.reviewType,
        };
        if (this.viewType === "Coach") {
          this.reportData.coach_id = this.userData?.id;
          this.reportData.userImage = this.userData?.userImage;
          this.reportData.name = this.userData?.name;
        }

        // Initialize traffic light data for new report
        if (this.isTrafficLightReview) {
          this.initializeTrafficLightData();
        }

        this.signatureState =
          CoachPerformanceReviewService.getDefaultSignatureState();
        this.originalReportData = JSON.parse(JSON.stringify(this.reportData));
        this.loading = false;
      }
    },

    initializeTrafficLightData(existingData = null) {
      const playerType = this.detectedPlayerType;

      // Handle combined traffic_light_data structure from API
      // The API returns { player: {...}, coach: {...} } in traffic_light_data
      if (existingData?.traffic_light_data) {
        let tlData =
          typeof existingData.traffic_light_data === "string"
            ? JSON.parse(existingData.traffic_light_data)
            : existingData.traffic_light_data;

        // Handle case where player/coach data might be strings (legacy double-encoded data)
        let playerData = tlData?.player;
        let coachData = tlData?.coach;

        if (typeof playerData === "string") {
          try {
            playerData = JSON.parse(playerData);
          } catch (e) {
            playerData = null;
          }
        }
        if (typeof coachData === "string") {
          try {
            coachData = JSON.parse(coachData);
          } catch (e) {
            coachData = null;
          }
        }

        this.trafficLightPlayerData =
          playerData || createEmptyTrafficLightData(playerType);
        this.trafficLightCoachData =
          coachData || createEmptyTrafficLightData(playerType);
      } else {
        this.trafficLightPlayerData = createEmptyTrafficLightData(playerType);
        this.trafficLightCoachData = createEmptyTrafficLightData(playerType);
      }
    },

    async loadSignatures(reportId) {
      // Guard: Don't call API if reportId is invalid
      if (!reportId || typeof reportId !== "number" || reportId <= 0) {
        console.log("Skipping loadSignatures - invalid reportId:", reportId);
        return;
      }

      try {
        const response =
          await CoachPerformanceReviewService.getSignatures(reportId);
        if (response.status === 200 && response.data?.length > 0) {
          response.data.forEach((signature) => {
            if (signature.type === "coachSignature") {
              this.signatureState.coach_signature_change = false;
              this.reportData.coach_signature_id = signature.id;
              this.reportData.coachSignature = signature.signature_url;
            }
            if (signature.type === "playerReportSignature") {
              this.signatureState.player_signature_change = false;
              this.reportData.player_signature_id = signature.id;
              this.reportData.playerReportSignature = signature.signature_url;
            }
            if (signature.type === "parentReportSignature") {
              this.signatureState.parent_signature_change = false;
              this.reportData.parent_signature_id = signature.id;
              this.reportData.parentReportSignature = signature.signature_url;
            }
          });
        }
      } catch (error) {
        console.error("Failed to load signatures:", error);
      }
    },

    async handleSave() {
      this.saving = true;
      try {
        const saveData = {
          ...this.reportData,
          user_id: this.playerData.id,
          season_start_date: this.seasonStartDate,
          season_end_date: this.seasonEndDate,
          review_type: this.isTrafficLightReview ? "traffic-light" : "text",
        };

        // Add traffic light data if applicable
        if (this.isTrafficLightReview) {
          saveData.traffic_light_player_data = JSON.stringify(
            this.trafficLightPlayerData,
          );
          saveData.traffic_light_coach_data = JSON.stringify(
            this.trafficLightCoachData,
          );
        }

        if (this.userData.userType !== "User") {
          saveData.coach_id = this.userData.id;
        }

        const response =
          await CoachPerformanceReviewService.saveReport(saveData);
        if (response.status === 200) {
          this.$toast?.info?.("Performance assessment saved", {
            variant: "success",
          });
          this.$emit("report-saved", response.data);
          this.isVisible = false;
        }
      } catch (error) {
        console.error("Failed to save report:", error);
        this.$toast?.error?.("Failed to save report");
      } finally {
        this.saving = false;
      }
    },

    async handleUpdate() {
      this.saving = true;
      try {
        const updateData = {
          ...this.reportData,
          review_type: this.isTrafficLightReview ? "traffic-light" : "text",
        };

        // Add traffic light data if applicable
        if (this.isTrafficLightReview) {
          updateData.traffic_light_player_data = JSON.stringify(
            this.trafficLightPlayerData,
          );
          updateData.traffic_light_coach_data = JSON.stringify(
            this.trafficLightCoachData,
          );
        }

        const response =
          await CoachPerformanceReviewService.updateReport(updateData);
        if (response.status === 200) {
          this.$toast?.info?.("Performance assessment updated", {
            variant: "success",
          });
          this.$emit("report-updated", this.reportData);
          this.isVisible = false;
        }
      } catch (error) {
        console.error("Failed to update report:", error);
        this.$toast?.error?.("Failed to update report");
      } finally {
        this.saving = false;
      }
    },

    async handlePublish() {
      this.saving = true;
      try {
        const publishData = {
          ...this.reportData,
          review_type: this.isTrafficLightReview ? "traffic-light" : "text",
        };

        // Add traffic light data if applicable
        if (this.isTrafficLightReview) {
          publishData.traffic_light_player_data = JSON.stringify(
            this.trafficLightPlayerData,
          );
          publishData.traffic_light_coach_data = JSON.stringify(
            this.trafficLightCoachData,
          );
        }

        const response = await CoachPerformanceReviewService.publishReport(
          publishData,
        );
        if (response.status === 200) {
          this.reportData.published = 1;
          this.reportData.status = "published";
          this.$toast?.info?.("Report published successfully", {
            variant: "success",
          });
          this.$emit("report-published", this.reportData);
        }
      } catch (error) {
        console.error("Failed to publish report:", error);
        this.$toast?.error?.("Failed to publish report");
      } finally {
        this.saving = false;
      }
    },

    async handleUnpublish() {
      this.saving = true;
      try {
        const unpublishData = {
          ...this.reportData,
          review_type: this.isTrafficLightReview ? "traffic-light" : "text",
        };

        // Add traffic light data if applicable
        if (this.isTrafficLightReview) {
          unpublishData.traffic_light_player_data = JSON.stringify(
            this.trafficLightPlayerData,
          );
          unpublishData.traffic_light_coach_data = JSON.stringify(
            this.trafficLightCoachData,
          );
        }

        const response = await CoachPerformanceReviewService.unpublishReport(
          unpublishData,
        );
        if (response.status === 200) {
          this.reportData.published = 0;
          this.reportData.status = "live";
          this.$toast?.info?.("Report unpublished", { variant: "warning" });
          this.$emit("report-unpublished", this.reportData);
        }
      } catch (error) {
        console.error("Failed to unpublish report:", error);
        this.$toast?.error?.("Failed to unpublish report");
      } finally {
        this.saving = false;
      }
    },

    async handleDelete() {
      if (
        !confirm(
          "Are you sure you want to delete this report? This action cannot be undone.",
        )
      ) {
        return;
      }
      this.saving = true;
      try {
        const response = await CoachPerformanceReviewService.deleteReport(
          this.reportData.id,
        );
        if (response.status === 200) {
          this.$toast?.info?.("Report deleted", { variant: "warning" });
          this.$emit("report-deleted", this.reportData.id);
          this.isVisible = false;
        }
      } catch (error) {
        console.error("Failed to delete report:", error);
        this.$toast?.error?.("Failed to delete report");
      } finally {
        this.saving = false;
      }
    },

    handleStatusChange() {
      // Status change triggers an update
      if (this.reportData.id) {
        this.handleUpdate();
      }
    },

    async handleSignatureSave({ signatureId, signatureData }) {
      this.savingSignature = signatureId;
      try {
        const response = await CoachPerformanceReviewService.saveSignature(
          signatureId,
          signatureData,
          this.reportData.id,
        );
        if (response.status === 200) {
          await this.loadSignatures(this.reportData.id);
          this.$toast?.info?.("Signature saved", { variant: "success" });
        }
      } catch (error) {
        console.error("Failed to save signature:", error);
        this.$toast?.error?.("Failed to save signature");
      } finally {
        this.savingSignature = null;
      }
    },

    handleSignatureEdit({ signatureId }) {
      // Just mark as editing - the SignatureCapture handles the rest
      const stateKey = this.getSignatureStateKey(signatureId);
      if (stateKey) {
        this.signatureState[stateKey] = true;
      }
    },

    handleSignatureCancel({ signatureId }) {
      const stateKey = this.getSignatureStateKey(signatureId);
      if (stateKey) {
        this.signatureState[stateKey] = false;
      }
    },

    getSignatureStateKey(signatureId) {
      const mapping = {
        playerReportSignature: "player_signature_change",
        parentReportSignature: "parent_signature_change",
        coachSignature: "coach_signature_change",
      };
      return mapping[signatureId];
    },

    handlePrint() {
      const pdf = this.generatePDF();
      this.pdfPreviewUrl = pdf.output("datauristring");
      this.showPdfPreview = true;
    },

    generatePDF() {
      const pdf = new JsPDF("p", "mm", "a4");
      const pageWidth = 210;
      const margin = 15;
      const contentWidth = pageWidth - margin * 2;
      let yPosition = margin;

      // Helper functions
      const addHeader = () => {
        pdf.setFillColor(0, 85, 150);
        pdf.rect(0, 0, pageWidth, 40, "F");

        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(24);
        pdf.setFont(undefined, "bold");
        pdf.text("Performance Review", margin, 25);

        pdf.setFontSize(12);
        pdf.setFont(undefined, "normal");
        pdf.text(
          this.formatDate(this.reportData.created_date),
          pageWidth - margin,
          25,
          { align: "right" },
        );

        pdf.setTextColor(0, 0, 0);
        yPosition = 50;
      };

      const addSection = (title, content, icon = "") => {
        // Check if we need a new page
        if (yPosition > 250) {
          pdf.addPage();
          yPosition = margin;
        }

        pdf.setFontSize(14);
        pdf.setFont(undefined, "bold");
        pdf.setTextColor(0, 85, 150);
        pdf.text(title, margin, yPosition);
        yPosition += 8;

        pdf.setFontSize(11);
        pdf.setFont(undefined, "normal");
        pdf.setTextColor(60, 60, 60);

        // Strip HTML tags for PDF
        const cleanContent = content
          ? content.replace(/<[^>]*>/g, "")
          : "No comments provided";
        const lines = pdf.splitTextToSize(cleanContent, contentWidth);
        pdf.text(lines, margin, yPosition);
        yPosition += lines.length * 6 + 10;
      };

      const addPlayerInfo = () => {
        pdf.setFontSize(16);
        pdf.setFont(undefined, "bold");
        pdf.setTextColor(0, 0, 0);
        pdf.text("Player Information", margin, yPosition);
        yPosition += 10;

        pdf.setFontSize(11);
        pdf.setFont(undefined, "normal");
        const playerInfo = [
          `Name: ${this.playerData.name || "N/A"}`,
          `Position: ${this.playerData.position || "N/A"}`,
          `DOB: ${this.formatDate(this.playerData.dob)}`,
          `Email: ${this.playerData.email || "N/A"}`,
        ];
        playerInfo.forEach((info) => {
          pdf.text(info, margin, yPosition);
          yPosition += 6;
        });
        yPosition += 10;
      };

      // Build PDF
      addHeader();
      addPlayerInfo();

      // Player Review
      pdf.setFontSize(18);
      pdf.setFont(undefined, "bold");
      pdf.setTextColor(0, 85, 150);
      pdf.text("Player Self-Assessment", margin, yPosition);
      yPosition += 12;

      addSection("Technical", this.reportData.technical_player_comments);
      addSection("Tactical", this.reportData.tactical_player_comments);
      addSection("Physical", this.reportData.physical_player_comments);
      addSection("Mental", this.reportData.physcological_player_comments);
      addSection("Overall Comments", this.reportData.player_comments);

      // Coach Review
      if (yPosition > 200) {
        pdf.addPage();
        yPosition = margin;
      }

      pdf.setFontSize(18);
      pdf.setFont(undefined, "bold");
      pdf.setTextColor(0, 85, 150);
      pdf.text("Coach Assessment", margin, yPosition);
      yPosition += 12;

      pdf.setFontSize(11);
      pdf.setFont(undefined, "normal");
      pdf.setTextColor(0, 0, 0);
      pdf.text(`Coach: ${this.reportData.name || "N/A"}`, margin, yPosition);
      yPosition += 10;

      addSection("Technical", this.reportData.technical_coach_comments);
      addSection("Tactical", this.reportData.tactical_coach_comments);
      addSection("Physical", this.reportData.physical_coach_comments);
      addSection("Mental", this.reportData.physcological_coach_comments);
      addSection("Overall Comments", this.reportData.coach_comments);

      // Footer with signatures note
      if (yPosition > 250) {
        pdf.addPage();
        yPosition = margin;
      }

      yPosition += 10;
      pdf.setFontSize(10);
      pdf.setTextColor(100, 100, 100);
      pdf.text("Signatures on file: Player, Parent, Coach", margin, yPosition);

      // Add page numbers
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(10);
        pdf.setTextColor(150, 150, 150);
        pdf.text(`Page ${i} of ${pageCount}`, pageWidth - margin, 290, {
          align: "right",
        });
      }

      return pdf;
    },

    formatDate(date) {
      if (!date) return "N/A";
      return moment(date).format("DD/MM/YYYY");
    },

    getImageUrl(pic) {
      if (!pic) return "";
      if (pic.startsWith("http") || pic.startsWith("data:")) {
        return pic;
      }
      return `/images/${pic}`;
    },

    onModalHidden() {
      // Reset state when modal closes
      this.reportData = CoachPerformanceReviewService.getEmptyReportData();
      this.signatureState =
        CoachPerformanceReviewService.getDefaultSignatureState();
      this.originalReportData = null;
      this.trafficLightPlayerData = null;
      this.trafficLightCoachData = null;
    },
  },
};
</script>

<style scoped>
.coach-performance-review-modal {
  --primary-color: #005596;
  --success-color: #28a745;
  --warning-color: #ffc107;
  --danger-color: #dc3545;
}

/* Traffic Light Review Styles */
.traffic-light-review {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
}

.traffic-light-header {
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #dee2e6;
}

.player-type-indicator {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.35rem 0.75rem;
  background: #e9ecef;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
  color: #495057;
}

.player-type-indicator i {
  font-size: 1rem;
}

.review-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #eee;
}

.review-meta {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin: 0;
}

.review-date {
  font-weight: 600;
  color: #333;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
  text-transform: capitalize;
}

.status-draft {
  background-color: #e9ecef;
  color: #495057;
}

.status-live {
  background-color: #d4edda;
  color: #155724;
}

.status-published {
  background-color: #cce5ff;
  color: #004085;
}

.autosave-indicator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: #856404;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  gap: 1rem;
}

.signed-report {
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
}

.player-signed-report,
.coach-signed-report {
  background: #fff;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.section-header {
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid var(--primary-color);
}

.section-header h4 {
  margin: 0;
  color: var(--primary-color);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.user-info-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
}

.user-photo-info img {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid var(--primary-color);
}

.user-details-pop p {
  margin: 0.25rem 0;
}

.user-details-pop .label {
  font-weight: 600;
  color: #555;
}

.review-wrapper {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.review-section {
  background: #fafafa;
  border-radius: 8px;
  padding: 1rem;
  border-left: 4px solid var(--primary-color);
}

.review-section .form__label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: var(--primary-color);
  margin-bottom: 0.75rem;
}

.readonly-content {
  padding: 0.75rem;
  background: #fff;
  border-radius: 4px;
  border: 1px solid #e9ecef;
  min-height: 60px;
}

.overall-comments {
  border-left-color: var(--success-color);
}

.overall-comments .form__label {
  color: var(--success-color);
}

.no-report-message {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.5rem;
  background: #fff3cd;
  border-radius: 8px;
  color: #856404;
}

.no-report-message i {
  font-size: 1.5rem;
}

.signature-section {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e9ecef;
}

.action-footer {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 2px solid #eee;
}

.status-select {
  max-width: 200px;
}

.pdf-preview-container {
  width: 100%;
  height: 80vh;
}

.pdf-iframe {
  width: 100%;
  height: 100%;
  border: none;
}

/* Responsive */
@media (max-width: 992px) {
  .signed-report {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .review-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .review-meta {
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .review-date {
    font-size: 0.9rem;
  }

  .status-badge {
    font-size: 0.75rem;
    padding: 0.2rem 0.5rem;
  }

  .player-signed-report,
  .coach-signed-report {
    padding: 1rem;
    border-radius: 8px;
  }

  .section-header {
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
  }

  .section-header h4 {
    font-size: 1.1rem;
  }

  .user-info-card {
    flex-direction: column;
    text-align: center;
    padding: 0.75rem;
  }

  .user-info-card.two-col-pop {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .user-photo-info {
    margin-bottom: 0.75rem;
  }

  .user-photo-info img {
    width: 60px;
    height: 60px;
  }

  .user-details-pop {
    width: 100%;
  }

  .user-details-pop p {
    font-size: 0.85rem;
    display: flex;
    justify-content: space-between;
    padding: 0.25rem 0;
    border-bottom: 1px solid #eee;
  }

  .user-details-pop p:last-child {
    border-bottom: none;
  }

  .review-wrapper {
    gap: 1rem;
  }

  .review-section {
    padding: 0.75rem;
  }

  .action-footer {
    flex-direction: column;
    gap: 0.5rem;
    padding-top: 1rem;
    margin-top: 1rem;
  }

  .action-footer .btn {
    width: 100%;
    padding: 0.75rem;
  }

  .status-select {
    max-width: 100%;
    width: 100%;
  }

  .signature-section {
    margin-top: 1rem;
    padding-top: 1rem;
  }

  .no-report-message {
    flex-direction: column;
    text-align: center;
    padding: 1rem;
  }

  .no-report-message i {
    font-size: 1.25rem;
  }

  .no-report-message p {
    margin: 0;
    font-size: 0.9rem;
  }

  .loading-state {
    padding: 2rem;
  }

  .loading-state p {
    font-size: 0.9rem;
  }

  .pdf-preview-container {
    height: 60vh;
  }
}

@media (max-width: 480px) {
  .player-signed-report,
  .coach-signed-report {
    padding: 0.75rem;
    margin: 0 -0.5rem;
    border-radius: 0;
    box-shadow: none;
    border-bottom: 1px solid #eee;
  }

  .section-header h4 {
    font-size: 1rem;
  }

  .user-photo-info img {
    width: 50px;
    height: 50px;
    border-width: 2px;
  }

  .user-details-pop p {
    font-size: 0.8rem;
  }

  .review-section {
    border-left-width: 3px;
    padding: 0.5rem;
  }

  .action-footer .btn {
    font-size: 0.9rem;
  }

  .autosave-indicator {
    font-size: 0.75rem;
  }

  .autosave-indicator span {
    display: none;
  }
}
</style>

<!-- Unscoped styles for Bootstrap modal overrides -->
<style>
/* Full screen modal on mobile */
.coach-performance-review-modal .modal-dialog {
  max-width: 100%;
  margin: 0;
  min-height: 100vh;
  max-height: 100vh;
}

.coach-performance-review-modal .modal-content {
  min-height: 100vh;
  max-height: 100vh;
  border: none;
  border-radius: 0;
  display: flex;
  flex-direction: column;
}

.coach-performance-review-modal .modal-header {
  flex-shrink: 0;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #dee2e6;
}

.coach-performance-review-modal .modal-body {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 1rem;
  -webkit-overflow-scrolling: touch;
}

.coach-performance-review-modal .modal-title {
  font-size: 1.1rem;
  font-weight: 600;
}

/* Tablet and larger - allow larger modal */
@media (min-width: 768px) {
  .coach-performance-review-modal .modal-dialog {
    max-width: 95vw;
    margin: 1rem auto;
    min-height: auto;
    max-height: calc(100vh - 2rem);
  }

  .coach-performance-review-modal .modal-content {
    min-height: auto;
    max-height: calc(100vh - 2rem);
    border-radius: 0.5rem;
  }

  .coach-performance-review-modal .modal-header {
    padding: 1rem 1.5rem;
  }

  .coach-performance-review-modal .modal-body {
    padding: 1.5rem;
  }

  .coach-performance-review-modal .modal-title {
    font-size: 1.25rem;
  }
}

/* Large screens - constrain width */
@media (min-width: 1200px) {
  .coach-performance-review-modal .modal-dialog {
    max-width: 1140px;
  }
}

/* iOS Safari fixes */
@supports (-webkit-touch-callout: none) {
  .coach-performance-review-modal .modal-dialog {
    min-height: -webkit-fill-available;
  }

  .coach-performance-review-modal .modal-content {
    min-height: -webkit-fill-available;
  }
}
</style>
